import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { es } from "date-fns/locale";
import { Sunrise, Sunset, MapPin, Users } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { t } from "@/i18n";

const toIso = (d) => {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fromIso = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};

export const BookingCalendar = ({ lang, party: partyRaw, onPartyChange, initial, onBack, onSubmit }) => {
  const L = t[lang];
  const party = parseInt(partyRaw, 10) || 1;
  const [availability, setAvailability] = useState([]);
  const [selectedDate, setSelectedDate] = useState(fromIso(initial?.date));
  const [slot, setSlot] = useState(initial?.slot || null);

  useEffect(() => {
    api.get(`/availability?language=${lang}`).then((r) => setAvailability(r.data.dates));
  }, [lang]);

  const availMap = useMemo(() => {
    const m = {};
    availability.forEach((a) => (m[a.date] = a));
    return m;
  }, [availability]);

  const slotFits = (s) => s.open && party <= s.available;
  const dateHasOpenSlot = (a) => a && a.slots.some(slotFits);

  const selectableSet = useMemo(() => {
    const s = new Set();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    availability.forEach((a) => {
      const [y, m, d] = a.date.split("-").map(Number);
      if (new Date(y, m - 1, d) < today) return;
      if (dateHasOpenSlot(a)) s.add(a.date);
    });
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availability, party]);

  const availableDateObjs = useMemo(
    () => Array.from(selectableSet).map((iso) => {
      const [y, m, d] = iso.split("-").map(Number);
      return new Date(y, m - 1, d);
    }),
    [selectableSet]
  );

  const defaultMonth = (() => {
    const today = new Date();
    let dm = new Date(today.getFullYear(), today.getMonth(), 1);
    const minM = new Date(2026, 6, 1);
    const maxM = new Date(2026, 10, 1);
    if (dm < minM) dm = minM;
    if (dm > maxM) dm = maxM;
    return dm;
  })();

  const isDisabled = (day) => !selectableSet.has(toIso(day));

  const current = selectedDate ? availMap[toIso(selectedDate)] : null;

  const handleSelectDate = (d) => {
    setSelectedDate(d);
    if (!d) return;
    const a = availMap[toIso(d)];
    const firstOpen = a?.slots.find(slotFits);
    setSlot(firstOpen ? firstOpen.key : null);
  };

  const freeSpots = (a) => {
    const opts = a.slots.filter((s) => s.open).map((s) => s.available);
    return opts.length ? Math.max(...opts) : 0;
  };

  const DayContent = (props) => {
    const iso = toIso(props.date);
    const a = availMap[iso];
    const spots = a ? freeSpots(a) : null;
    const color = spots == null ? "" : spots === 0 ? "text-red-400" : spots <= 5 ? "text-amber-600" : "text-emerald-600";
    return (
      <div className="flex flex-col items-center justify-center leading-none">
        <span>{props.date.getDate()}</span>
        {a && <span className={`text-[9px] font-semibold mt-0.5 ${color}`}>{spots}</span>}
      </div>
    );
  };

  const renderSlot = (s) => {
    const Icon = s.time.startsWith("09") ? Sunrise : Sunset;
    const enabled = slotFits(s);
    const lockedOther = s.lang && s.lang !== lang;
    const statusText = s.full ? L.slotFull : lockedOther ? L.otherLangSlot : L.spotsLeft(s.available);
    return (
      <button
        key={s.key}
        type="button"
        data-testid={`slot-${s.key}`}
        disabled={!enabled}
        onClick={() => setSlot(s.key)}
        className={`text-left p-4 rounded-xl border transition-colors duration-200 ${
          !enabled
            ? "opacity-50 cursor-not-allowed border-coastal-secondary bg-coastal-secondary/10"
            : slot === s.key
            ? "border-coastal-primary bg-coastal-primary/10"
            : "border-coastal-secondary bg-white hover:border-coastal-primary"
        }`}
      >
        <div className="flex items-center gap-2 text-coastal-primary font-semibold">
          <Icon size={18} /> {s.time}
        </div>
        <p className="text-xs mt-1.5 font-medium text-coastal-text">{statusText}</p>
      </button>
    );
  };

  const selectedSlot = current?.slots.find((s) => s.key === slot);
  const canSubmit = selectedDate && slot && selectedSlot && slotFits(selectedSlot);

  const changeParty = (raw) => {
    const v = raw.replace(/[^0-9]/g, "");
    if (v === "") return onPartyChange("");
    onPartyChange(Math.min(parseInt(v, 10), 15));
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.35 }}
      className="w-full max-w-xl mx-auto"
      data-testid="booking-calendar"
    >
      <p className="text-sm font-medium text-coastal-accent tracking-wide uppercase mb-2">{L.stepDate}</p>
      <h2 className="font-heading text-3xl sm:text-4xl text-coastal-text mb-2">{L.selectDate}</h2>
      <p className="text-coastal-muted text-sm mb-1">{L.dateHint}</p>
      <p className="text-coastal-primary text-xs mb-2 font-medium">{L.availWeekdays}</p>
      <div className="mb-4 flex items-end gap-4">
        <div>
          <Label className="text-coastal-text font-medium">{L.people}</Label>
          <Input
            data-testid="input-people"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={partyRaw}
            onChange={(e) => changeParty(e.target.value)}
            onBlur={(e) => { if (!parseInt(e.target.value, 10)) onPartyChange(1); }}
            className="mt-1.5 rounded-lg border-coastal-secondary focus:ring-2 focus:ring-coastal-primary w-28"
          />
        </div>
        <p className="text-xs text-coastal-muted pb-2">{L.peopleHint}</p>
      </div>
      <p className="text-coastal-muted text-xs mb-6 flex items-center gap-1.5">
        <Users size={13} /> {party} · {L.spotsLegend}
      </p>

      <div className="bg-white rounded-2xl border border-coastal-secondary p-4 shadow-[0_8px_32px_rgba(0,0,0,0.06)] flex justify-center">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelectDate}
          disabled={isDisabled}
          defaultMonth={defaultMonth}
          fromMonth={new Date(2026, 6, 1)}
          toMonth={new Date(2026, 10, 30)}
          locale={lang === "es" ? es : undefined}
          data-testid="date-picker"
          modifiers={{ available: availableDateObjs }}
          modifiersClassNames={{ available: "ring-1 ring-coastal-primary/50 text-coastal-primary font-semibold" }}
          classNames={{
            head_cell: "text-muted-foreground rounded-md w-11 font-normal text-[0.8rem]",
            cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected])]:rounded-md",
            day: "h-11 w-11 p-0 rounded-md font-normal hover:bg-coastal-primary/15 transition-colors aria-selected:opacity-100",
            day_disabled: "text-muted-foreground opacity-40 font-normal hover:bg-transparent cursor-not-allowed ring-0",
            day_outside: "text-muted-foreground opacity-30 ring-0",
            day_selected: "bg-coastal-primary text-white hover:bg-coastal-primary hover:text-white focus:bg-coastal-primary ring-0",
          }}
          components={{ DayContent }}
        />
      </div>

      {current && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
          <p className="text-sm font-medium text-coastal-text mb-3">{L.schedule}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {current.slots.map(renderSlot)}
          </div>
        </motion.div>
      )}

      <div className="flex items-center gap-4 text-xs text-coastal-muted mt-5">
        <span className="flex items-center gap-1.5"><MapPin size={14} /> {L.meetingValue}</span>
      </div>

      <div className="flex gap-3 mt-8">
        <Button
          variant="outline"
          data-testid="calendar-back-btn"
          onClick={onBack}
          className="rounded-full border-coastal-secondary text-coastal-muted hover:text-coastal-text"
        >
          {L.back}
        </Button>
        <Button
          data-testid="confirm-booking-btn"
          disabled={!canSubmit}
          onClick={() => onSubmit(toIso(selectedDate), slot)}
          className="rounded-full bg-coastal-accent hover:bg-coastal-accent/90 text-white px-8 flex-1 transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-50"
        >
          {L.continueToDetails}
        </Button>
      </div>
    </motion.div>
  );
};

export default BookingCalendar;
